import { useTranslation } from "react-i18next";
import { Save, RefreshCcw, Trash2, SlidersHorizontal } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

import AdvancedFilterBuilder from "../AdvancedFilterBuilder";
import { FILTER_FIELDS } from "../../utils/employeeConstants";

const AdvancedFiltersDialog = ({
  open,
  onOpenChange,
  draftAdvancedFilters,
  setDraftAdvancedFilters,
  savedFilters,
  savedFiltersLoading,
  selectedSavedFilterId,
  newFilterName,
  setNewFilterName,
  newFilterDescription,
  setNewFilterDescription,
  savingFilter,
  handleSaveFilter,
  handleDeleteFilter,
  handleSelectSavedFilter,
  handleResetAdvancedFilters,
  handleApplyAdvancedFilters,
  loadSavedFilters,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <SlidersHorizontal className="h-5 w-5 text-primary" />
            <span>{t("employeesPage.dialogs.advancedFilters")}</span>
          </DialogTitle>
          <DialogDescription>
            {t("employeesPage.dialogs.advancedFiltersDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <div className="space-y-4">
            <AdvancedFilterBuilder
              value={draftAdvancedFilters}
              onChange={setDraftAdvancedFilters}
              fields={FILTER_FIELDS}
            />
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">{t("employeesPage.sections.savedFilters")}</h4>
                <Button variant="ghost" size="icon" onClick={loadSavedFilters}>
                  <RefreshCcw className="h-4 w-4" />
                </Button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2 border rounded-md p-3 bg-muted/60">
                {savedFiltersLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
                ) : savedFilters.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("employeesPage.messages.noSavedFilters")}</p>
                ) : (
                  savedFilters.map((filter) => (
                    <div
                      key={filter.id}
                      className={`border rounded-md p-3 space-y-2 ${
                        selectedSavedFilterId === filter.id ? "border-primary bg-primary/5" : "border-border bg-card"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{filter.name}</p>
                          {filter.description && (
                            <p className="text-xs text-muted-foreground">{filter.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleSelectSavedFilter(filter)}>
                            {t("employeesPage.actions.load")}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteFilter(filter.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h4 className="text-sm font-semibold">{t("employeesPage.sections.saveFilter")}</h4>
              <Input
                placeholder={t("employeesPage.placeholders.filterName")}
                value={newFilterName}
                onChange={(e) => setNewFilterName(e.target.value)}
              />
              <Textarea
                rows={3}
                placeholder={t("employeesPage.placeholders.filterDescription")}
                value={newFilterDescription}
                onChange={(e) => setNewFilterDescription(e.target.value)}
              />
              <Button onClick={handleSaveFilter} disabled={savingFilter}>
                <Save className="h-4 w-4 mr-2" />
                {savingFilter ? t("employeesPage.messages.savingFilter") : t("employeesPage.sections.saveFilter")}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={handleResetAdvancedFilters}>
            {t("employeesPage.actions.reset")}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleApplyAdvancedFilters}>
            {t("employeesPage.actions.applyFilters")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedFiltersDialog;
